import { template } from 'lodash-es'
import { trackComponent } from './tracking'

class ProductTable {
  constructor(tableWrapper) {
    this.$tableWrapper = tableWrapper
    this.variant = tableWrapper.dataset.variant
    this.$table = tableWrapper.querySelector('[data-product-table-v2]')
    // scenario where there are no results
    if (!this.$table) {
      return
    }
    this.$sorts = tableWrapper.querySelector('[name="sort"]')
    this.$filterBlock = tableWrapper.querySelector('[data-filter-block]')
    this.$calculatorBlock = tableWrapper.querySelector('[data-product-table-calculator]')
    this.$filterButton = this.$tableWrapper.querySelector(
      '.ProductTable__tableControlsButtons > button',
    )
    this.$calculatorButton = this.$calculatorBlock?.querySelector('button[data-fs-product-table]')
    this.calculatorButtonEnabled = true
    this.$compliance = tableWrapper.querySelector('[data-product-table-v2-compliance]')
    this.$loadMore = tableWrapper.querySelector('[data-product-table-v2-load-more]>button')
    this.$travelMoneyCurrency = tableWrapper.querySelector('[data-travel-money-currency]')
    this.$travelMoneyCurrencies = tableWrapper.querySelector('[data-travel-money-currencies]')
    this.$eligibilityModal = tableWrapper.querySelector('[data-product-table-v2-eligibility-modal]')
    this.schemaId = this.$table.dataset.schemaId
    this.schema = window.__initialState__.contentful.productTables[this.schemaId]
    this.trackedRows = []
    this.emptyField = false

    this.transferAmount = null
    this.monthlyPayments = null

    this.eligibilityInformation = null
    this.scoredEligibilityIds = null

    this.intersectionOptions = { threshold: 0.5 }

    if (this.variant === 'eligibility') {
      this.setupEligibilityModal()
      this.addPreapprovedClickHandlers()
    }

    this.trackMoreInformation()
    this.trackRowIntersection()

    this.productRowCount = this.$tableWrapper.querySelectorAll('.TableRow')?.length || 0

    if (this.$sorts) {
      this.initSorts()
    }
    if (this.$loadMore) {
      this.initLoadMore()
    }

    if (this.$filterButton) {
      this.setSubmitHandler(this.$filterBlock, 'filter')
      this.$filterButton.addEventListener('click', () => this.toggleFilterBlock())
    } else if (this.$calculatorBlock) {
      this.setSubmitHandler(this.$filterBlock, 'calculator')
    }

    this.getCalculatorValues()

    if (this.$travelMoneyCurrency) {
      this.initTravelMoneyCurrency()
    }

    if (this.$travelMoneyCurrencies) {
      this.initTravelMoneyCurrencies()
    }
  }

  setupEligibilityModal() {
    this.$eligibilityModal.addEventListener('click', () => {
      this.$eligibilityModal.style.display = 'none'
      document.body.style.overflow = 'auto'
    })
  }

  addPreapprovedClickHandlers() {
    const preapprovedElements = this.$table.querySelectorAll('[data-preapproved-click]')
    preapprovedElements.forEach((preapprovedElement) => {
      if (preapprovedElement.getAttribute('data-preapproved-click') === 'true') {
        return
      }
      preapprovedElement.setAttribute('data-preapproved-click', 'true')
      preapprovedElement.addEventListener('click', () => {
        this.$eligibilityModal.style.display = 'flex'
        document.body.style.overflow = 'hidden'
        const row = preapprovedElement.closest('[data-product-id]')
        const productId = row.getAttribute('data-product-id')

        this.trackInteraction('preapprovedClicked', { product_id: productId })
      })
    })
  }

  getSortLabel() {
    const { children, selectedIndex } = this.$sorts
    return children[selectedIndex].innerText?.toLowerCase()
  }

  setLoadingState(isLoading) {
    this.$table.style = isLoading ? 'opacity: 0.5' : ''
  }

  scrollToProductTable() {
    this.$tableWrapper.scrollIntoView()
  }

  initSorts() {
    this.$sorts.addEventListener('click', () => {
      this.trackInteraction('sortClicked', { event: this.getSortLabel() })
    })

    this.$sorts.addEventListener('change', async () => {
      const sortLabel = this.getSortLabel()
      if (sortLabel === 'sort') {
        return
      }

      this.setLoadingState(true)

      this.trackInteraction('sortApplied', { event: this.getSortLabel() })

      this.scrollToProductTable()

      const response = await this.request()

      this.productRowCount = response.data.length
      this.updateComplianceText(response)

      this.$table.innerHTML = response.table
      this.addPreapprovedClickHandlers()

      this.setLoadingState(false)

      this.trackMoreInformation()
      this.trackRowIntersection()
    })
  }

  toggleFilterBlock() {
    this.$filterBlock.classList.toggle('ProductTable__filterBlock--visible')
  }

  setSubmitHandler(filterOrCalculatorBlock, type) {
    const btn = filterOrCalculatorBlock.querySelector('button[type="button"]')

    btn.addEventListener('click', async () => {
      if (!this.calculatorButtonEnabled) {
        return
      }

      if (type === 'filter') {
        this.toggleFilterBlock()
      }

      this.scrollToProductTable()
      this.setLoadingState(true)

      const response = await this.request()

      this.productRowCount = response.data.length
      this.$table.innerHTML = response.table

      if (this.$loadMore) {
        const totalHits = response.totalHits
        const remainingHits = totalHits - this.productRowCount
        const maxPerPage = this.schema.fields.schema.maxPerPage
        const loadMoreText = `Load ${Math.min(maxPerPage, remainingHits)} more`
        this.$loadMore.innerText = loadMoreText

        this.$loadMore.style.display = remainingHits < 1 ? 'none' : 'block'
      }

      if (response.totalHits < 1) {
        this.$filterBlock.classList.add('ProductTable__filterBlock--visible')
        this.$filterBlock.querySelector('.ProductTable__filterBlock__noResults').style.display =
          'flex'
      } else {
        this.$filterBlock.querySelector('.ProductTable__filterBlock__noResults').style.display =
          'none'
      }

      this.updateComplianceText(response)

      this.setLoadingState(false)
    })
  }

  trackLoadMore(response, remainingHits) {
    this.trackInteraction('loadMoreClicked', {
      newProducts: remainingHits,
      productIds: response.data.map((d) => d._source.definition.id).join(';'),
      productNames: response.data.map((d) => d._source.definition.display_name).join(';'),
    })
  }

  initLoadMore() {
    this.$loadMore?.addEventListener('click', async () => {
      this.setLoadingState(true)

      const response = await this.request({
        pageOffset: this.productRowCount,
      })

      const template = document.createElement('template')
      template.innerHTML = response.table
      const newRows = template.content.lastElementChild?.querySelectorAll('.TableRow')
      newRows?.forEach((row) => {
        this.$table.appendChild(row)
      })
      const offset = this.productRowCount
      this.productRowCount += response.data.length
      const totalHits = response.totalHits
      const maxPerPage = this.schema.fields.schema.maxPerPage

      const remainingHits = totalHits - this.productRowCount

      const loadMoreText = `Load ${Math.min(maxPerPage, remainingHits)} more`
      this.$loadMore.innerText = loadMoreText
      if (remainingHits < 1) {
        this.$loadMore.style.display = 'none'
      }
      this.trackMoreInformation(offset)
      this.trackRowIntersection(offset)
      this.trackLoadMore(response, remainingHits)
      this.addPreapprovedClickHandlers()
      this.setLoadingState(false)
    })
  }

  updateComplianceText(response) {
    const sortBy = this.$sorts.options[this.$sorts.selectedIndex].text

    const compiledComplianceText = template(this.schema.fields.schema.complianceText)
    const updatedComplianceText = compiledComplianceText({
      resultCount: response.totalHits,
      resultOrResults: response.totalHits === 1 ? 'result' : 'results',
      sortBy,
      complianceLink: '"/how-we-order-our-comparisons"',
      transferAmount: this.transferAmount,
      monthlyPayments: this.monthlyPayments,
    })

    this.$compliance.innerHTML = updatedComplianceText
  }

  async request(body = {}) {
    const finalBody = {
      calculatorId: this.schema.fields.schema.calculatorId,
      queryId: this.$table.dataset.datasetId,
      schemaId: this.schemaId,
      sortLabel: this.getSortLabel(),
      filters: this.getFilterValues().concat(this.getCalculatorValues()),
      maxPerPage: this.schema.fields.schema.maxPerPage,
      productTableId: this.$table.dataset.contentfulTableId,
      noResultsId: this.$table.dataset.noResultsId,
      brand: window.__BRAND__,
      page: window.location.pathname,
      ...body,
    }

    if (this.emptyField === true) {
      this.$filterBlock.querySelector('.ProductTable__filterBlock__noResults').style.display =
        'flex'
      return
    }

    const response = await fetch('/fs/elastic/table-v2', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalBody),
    })
    const result = await response.json()
    return result
  }

  getFilterValues() {
    const filters = []
    if (this.$filterBlock) {
      const initialFilter = this.schema.fields.schema.filters
      const filterFields =
        this.$filterBlock?.querySelectorAll('.ProductTable__filterBlock__fields > .Field') ?? []

      for (const [index, filterObj] of initialFilter.entries()) {
        if (filterObj.type === 'dropdown') {
          const value = filterFields[index].querySelector('select').value
          filters.push({
            ...filterObj,
            options: filterObj.options.map((option) => {
              return option.label === value ? { ...option, value: true } : option
            }),
          })
        } else if (filterObj.type === 'checkbox') {
          filters.push({
            ...filterObj,
            options: filterObj.options.map((option, i) => {
              const checked =
                filterFields[index].querySelectorAll('input[type="checkbox"]')[i].checked
              return checked ? { ...option, value: true } : option
            }),
          })
        }
      }
    }
    return filters
  }

  getCalculatorValues() {
    const calculatorResult = []
    if (this.$calculatorBlock) {
      const result = {
        type: 'calculator',
        id: this.schema.fields.schema.calculatorId,
        usedBy: [this.$table.dataset.contentfulTableId],
        inputs: {},
      }

      const calculatorFields =
        this.$calculatorBlock?.querySelectorAll('.ProductTable__filterBlock__fields > .Field') ?? []

      const fields = this.schema.fields.schema.calculator.fields

      for (const fw of calculatorFields) {
        const domLabel = fw.querySelector('label').innerText
        const field = fields.find((f) => f.label === domLabel)
        if (field) {
          if (field.type === 'dropdown') {
            const domElement = fw.querySelector('select')
            const selectedOption = Array.from(domElement.options).find((option) => option.selected)
            result.inputs[field.name] = selectedOption.value
          } else if (field.type === 'input') {
            const domElement = fw.querySelector('input')
            if (!domElement.value) {
              this.emptyField = true
            }
            result.inputs[field.name] = domElement.value
            if (domElement.name === 'transferAmount') {
              this.transferAmount = domElement.value
            }
            if (domElement.name === 'monthlyPayments') {
              this.monthlyPayments = domElement.value
            }
          } else if (field.type === 'radio') {
            const radios = fw.querySelectorAll('input[type="radio"]')
            const checkedRadio = Array.from(radios).find((radio) => radio.checked)
            result.inputs[checkedRadio.name] = checkedRadio.value
          } else {
            // eslint-disable-next-line no-console
            console.log('unknown field type:', field.type)
          }
        }
      }
      if (this.$travelMoneyCurrency) {
        const input = this.$travelMoneyCurrency.querySelector('input[type="number"]')
        const amount = input.value
        const currencyMap = JSON.parse(this.$travelMoneyCurrency.dataset.travelMoneyCurrency)
        const currencyPair = currencyMap[0].currency
        const radios = this.$travelMoneyCurrency.querySelectorAll(
          'input[type="radio"][name="currencyToUse"]',
        )
        const sourceCurrency = Array.from(radios).find((radio) => radio.checked).value

        result.inputs.currencyPair = currencyPair
        result.inputs.currencyToUse = sourceCurrency
        result.inputs[input.name] = amount
      }

      if (this.$travelMoneyCurrencies) {
        const input = this.$travelMoneyCurrencies.querySelector('input[type="number"]')
        const select = this.$travelMoneyCurrencies.querySelector('select')

        const currencyToUse = input.name === 'gbpAmount' ? 'GBP' : select.value

        result.inputs.currencyPair = select.value
        result.inputs.currencyToUse = currencyToUse
        result.inputs.gbpAmount = input.value
        result.inputs.otherAmount = input.value
      }
      calculatorResult.push(result)
    }
    return calculatorResult
  }

  initTravelMoneyCurrency() {
    const currencyMap = JSON.parse(this.$travelMoneyCurrency.dataset.travelMoneyCurrency)

    const input = this.$travelMoneyCurrency.querySelector('input[type="number"]')
    input.addEventListener('input', (e) => this.travelMoneyCurrencyInputHandler(e, input))
    const prefix = this.$travelMoneyCurrency.querySelector(
      'span[data-input-prefix-selector-content]',
    )
    const radios = this.$travelMoneyCurrency.querySelectorAll(
      'input[type="radio"][name="currencyToUse"]',
    )
    radios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        let currencySymbol, inputName
        if (e.target.value === 'GBP') {
          currencySymbol = '£'
          inputName = 'gbpAmount'
        } else {
          currencySymbol = currencyMap.find((c) => c.currency === e.target.value).prefix
          inputName = 'otherAmount'
        }
        prefix.innerText = currencySymbol
        input.name = inputName
      })
    })
  }

  travelMoneyCurrencyInputHandler(e, input) {
    const amount = parseFloat(e.target.value)
    const field = input.parentElement.parentElement
    let errorMessage = field.querySelector('[data-calculator-error-message]')
    if (isNaN(amount) || amount > 99999 || amount < 1) {
      this.$calculatorButton.classList.add('disabled')
      input.classList.add('error')
      this.calculatorButtonEnabled = false
      if (!errorMessage) {
        errorMessage = document.createElement('div')
        errorMessage.setAttribute('data-calculator-error-message', '')
        errorMessage.textContent = 'Please enter a value between 1 and 99999'
        errorMessage.classList.add('calculatorErrorMessage')
        field.appendChild(errorMessage)
      }
    } else {
      this.$calculatorButton.classList.remove('disabled')
      input.classList.remove('error')
      this.calculatorButtonEnabled = true
      if (errorMessage) {
        field.removeChild(errorMessage)
      }
    }
  }

  initTravelMoneyCurrencies() {
    const currencyMap = JSON.parse(this.$travelMoneyCurrencies.dataset.travelMoneyCurrencies)

    const input = this.$travelMoneyCurrencies.querySelector('input[type="number"]')
    input.addEventListener('input', (e) => this.travelMoneyCurrencyInputHandler(e, input))
    const prefix = this.$travelMoneyCurrencies.querySelector(
      'span[data-input-prefix-selector-content]',
    )
    const select = this.$travelMoneyCurrencies.querySelector('#currencyPair')
    select.addEventListener('change', (e) => {
      if (input.name === 'otherAmount') {
        const currencySymbol = currencyMap.find((c) => c.currency === e.target.value).prefix
        prefix.innerText = currencySymbol
      }
    })
    this.$travelMoneyCurrencies.querySelector('#swap').addEventListener('click', () => {
      const fields = document
        .querySelector('[data-travel-money-currencies]')
        .querySelectorAll('.Field')
      if (input.name === 'gbpAmount') {
        const currencySymbol = currencyMap.find((c) => c.currency === select.value).prefix
        prefix.innerText = currencySymbol
        input.name = 'otherAmount'
        fields[1].querySelector('label').innerText = 'To'
        fields[2].querySelector('label').innerText = 'From'
        fields[1].classList.remove('ProductTable__filterBlock__travelMoneyCurrencies-2')
        fields[1].classList.add('ProductTable__filterBlock__travelMoneyCurrencies-4')
        fields[2].classList.remove('ProductTable__filterBlock__travelMoneyCurrencies-4')
        fields[2].classList.add('ProductTable__filterBlock__travelMoneyCurrencies-2')
      } else {
        prefix.innerText = '£'
        input.name = 'gbpAmount'
        fields[1].querySelector('label').innerText = 'From'
        fields[2].querySelector('label').innerText = 'To'
        fields[1].classList.remove('ProductTable__filterBlock__travelMoneyCurrencies-4')
        fields[1].classList.add('ProductTable__filterBlock__travelMoneyCurrencies-2')
        fields[2].classList.remove('ProductTable__filterBlock__travelMoneyCurrencies-2')
        fields[2].classList.add('ProductTable__filterBlock__travelMoneyCurrencies-4')
      }
    })
  }

  trackInteraction(eventName, eventData?) {
    trackComponent(
      'Product Table Event',
      eventName,
      {
        component_category: 'eevee-component',
        component_id: this.$table.dataset.contentfulTableId,
        component_name: 'productTableV2',
        contentful_name: this.$table.dataset.contentfulName,
      },
      eventData,
    )
  }

  trackRowIntersection(offset = 0) {
    const intersectionObserver = new IntersectionObserver((entries, observer) => {
      entries
        .filter(
          (entry) =>
            entry.isIntersecting && entry.intersectionRatio >= this.intersectionOptions.threshold,
        )
        .forEach((entry) => {
          this.trackInteraction('viewed', {
            brand: this.brand,
            product_id: entry.target.getAttribute('data-product-id'),
            index: Array.from(this.$table.querySelectorAll('[data-product-id]')).indexOf(
              entry.target,
            ),
          })
          this.trackedRows.push(entry.target.getAttribute('data-product-id'))
          observer.unobserve(entry.target)
        })
    }, this.intersectionOptions)
    const rows = Array.from(this.$table.querySelectorAll('[data-product-id]')).slice(offset)
    for (const row of rows) {
      if (!this.trackedRows.includes(row.getAttribute('data-product-id'))) {
        intersectionObserver.observe(row)
      }
    }
  }

  trackMoreInformation(offset = 0) {
    Array.from(this.$table.querySelectorAll('[data-product-id]'))
      .slice(offset)
      .forEach((row, index) => {
        const productId = row.dataset.productId
        const $moreInformation = row.querySelector('[data-product-table-v2-more-information]')
        $moreInformation?.addEventListener('toggle', () => {
          const action = $moreInformation.open ? 'Opened' : 'Closed'
          this.trackInteraction('moreInformation ' + action, {
            product_id: productId,
            index,
          })
        })
      })
  }
}

document.querySelectorAll('[data-product-table-v2-wrapper]').forEach((productTableWrapper) => {
  return new ProductTable(productTableWrapper)
})
